import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

import { cn } from '@/lib/utils.ts';
import { type CarrierType, carrierTypeToFamily, prettyFamilyName } from '@/types/insurance.type.ts';
import { type Product, groupByCategory } from '@/types/product.type.ts';

type Coverage = 'covered' | 'pending' | 'not-covered';

export default function BenefitsListing({
  coveredCarrierTypes,
  pendingCarrierTypes,
  notCoveredCarrierTypes,
  coveredProducts,
  pendingProducts,
  notCoveredProducts,
}: {
  coveredCarrierTypes: Set<CarrierType>;
  pendingCarrierTypes: Set<CarrierType>;
  notCoveredCarrierTypes: CarrierType[];
  coveredProducts: Product[];
  pendingProducts: Product[];
  notCoveredProducts: Product[];
}) {
  return (
    <div className="flex flex-col gap-4">
      {coveredProducts.length > 0 && (
        <>
          <Heading coverage="covered" carrierTypes={coveredCarrierTypes} />
          <ProductListing coverage="covered" products={coveredProducts} />
        </>
      )}

      {pendingProducts.length > 0 && (
        <>
          <Heading coverage="pending" carrierTypes={pendingCarrierTypes} />
          <ProductListing coverage="pending" products={pendingProducts} />
        </>
      )}

      {notCoveredProducts.length > 0 && (
        <>
          <Heading
            coverage="not-covered"
            carrierTypes={new Set<CarrierType>(notCoveredCarrierTypes)}
          />
          <ProductListing coverage="not-covered" products={notCoveredProducts} />
        </>
      )}
    </div>
  );
}

function Heading({
  coverage,
  carrierTypes,
}: {
  coverage: Coverage;
  carrierTypes: Set<CarrierType>;
}) {
  const familyNames = [...new Set([...carrierTypes].map((c) => carrierTypeToFamily(c)))].map(
    (family) => prettyFamilyName(family)
  );

  if (carrierTypes.size === 0) return null;

  if (coverage === 'covered') {
    return (
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-spruce-500" />
        <p className="text-center text-sm font-medium text-spruce-500">
          100% COVERED BY YOUR {familyNames.map((n) => n.toUpperCase()).join(' / ')}
        </p>
        <div className="h-px flex-1 bg-spruce-500" />
      </div>
    );
  }

  if (coverage === 'pending') {
    return (
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-charcoal-400" />
        <p className="text-center text-sm font-medium text-charcoal-400">
          <span>PENDING COVERAGE </span>
          <span className="font-normal italic">(Requires {familyNames.join(' / ')})</span>
        </p>
        <div className="h-px flex-1 bg-charcoal-400" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-rose-500" />
      <p className="text-center text-sm font-medium text-rose-500">
        <span>NOT COVERED </span>
        <span className="font-normal italic">(Requires {familyNames.join(' / ')})</span>
      </p>
      <div className="h-px flex-1 bg-rose-500" />
    </div>
  );
}

function ProductListing({ coverage, products }: { coverage: Coverage; products: Product[] }) {
  const categoriesByUuid = useMemo(
    () => new Map(products.map((p) => [p.category.uuid, p.category])),
    [products]
  );
  const productsByCategory = useMemo(() => groupByCategory(products), [products]);
  const sizeSortedCategories = useMemo(() => {
    return [...productsByCategory.entries()]
      .map(([categoryUuid, products]) => ({ categoryUuid, products }))
      .sort((a, b) => b.products.length - a.products.length);
  }, [productsByCategory]);

  if (products.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-6">
      {sizeSortedCategories.map(({ categoryUuid, products }) => (
        <div key={categoryUuid} className="flex flex-col gap-1">
          <CategoryHeader
            categoryName={categoriesByUuid.get(categoryUuid)?.name || 'Products'}
            coverage={coverage}
          />
          {products.length > 0 && (
            <div className="flex pl-4">
              <ul className="flex-1 list-disc text-sm leading-[20px]">
                {firstColumn(products).map((product, index) => (
                  <li
                    key={product.uuid}
                    className={cn('ml-[21px]', index < firstColumn.length - 1 && 'mb-0')}
                  >
                    <span>{product.name}</span>
                  </li>
                ))}
              </ul>
              <ul className="flex-1 list-disc text-sm leading-[20px]">
                {secondColumn(products).map((product, index) => (
                  <li
                    key={product.uuid}
                    className={cn('ml-[21px]', index < secondColumn.length - 1 && 'mb-0')}
                  >
                    <span>{product.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function firstColumn(items: Product[]): Product[] {
  const midpoint = Math.ceil(items.length / 2);
  return items.slice(0, midpoint);
}

function secondColumn(items: Product[]): Product[] {
  const midpoint = Math.ceil(items.length / 2);
  return items.slice(midpoint);
}

function CategoryHeader({ coverage, categoryName }: { coverage: Coverage; categoryName: string }) {
  if (coverage === 'covered') {
    return (
      <div className="flex items-center gap-1">
        <Check className="size-4 shrink-0 text-spruce-500" />
        <p className="text-sm leading-[21px] font-medium text-spruce-500">{categoryName}</p>
      </div>
    );
  }

  if (coverage === 'pending') {
    return <p className="text-sm leading-[21px] font-medium text-charcoal-400">{categoryName}</p>;
  }

  return (
    <div className="flex items-center gap-1">
      <X className="size-4 shrink-0 text-rose-500" />
      <p className="text-sm leading-[21px] font-medium text-rose-500">{categoryName}</p>
    </div>
  );
}
